/**
 * Sticky Add to Cart Component
 * Handles visibility, variant sync, and interaction for sticky cart bar
 */

class StickyAddToCart {
  constructor() {
    this.stickyCart = document.querySelector(".js-sticky-cart");
    if (!this.stickyCart) return;

    this.productSection = this.stickyCart.closest("product-single");
    if (!this.productSection) return;

    this.mainForm = this.productSection.querySelector(".js-product-form");
    this.stickyForm = this.stickyCart.querySelector(".js-sticky-cart-form");
    this.stickyProductForm = this.stickyCart.querySelector("product-form");

    this.mainAddButton = this.mainForm?.querySelector(".js-product-add");
    this.stickyAddButton = this.stickyCart.querySelector(".js-sticky-cart-add");

    // Store text elements with the new structure
    this.mainAddText = this.mainAddButton?.querySelector(
      ".js-product-add-text"
    );
    this.stickyAddText = this.stickyAddButton?.querySelector(
      ".js-product-add-text"
    );

    this.mainVariantInput = this.mainForm?.querySelector('input[name="id"]');
    this.stickyVariantInput = this.stickyCart.querySelector(
      ".js-sticky-variant-id"
    );

    this.mainQuantityInput = this.mainForm?.querySelector(
      'input[name="quantity"]'
    );
    this.stickyQuantityInput = this.stickyCart.querySelector(
      ".js-sticky-quantity"
    );

    this.priceElement = this.stickyCart.querySelector(".js-sticky-cart-price");

    // Find variant selectors
    this.mainVariantSelector = this.productSection.querySelector(
      "variant-radios, variant-selects, variant-mixed-inputs"
    );
    this.stickyVariantSelector = this.stickyCart.querySelector(
      "variant-radios, variant-selects, variant-mixed-inputs"
    );

    this.lastScrollTop = 0;
    this.scrollThreshold = 300;
    this.isVisible = false;
    this.updatePaddingCallback = null;

    this.init();
  }

  init() {
    this.setupScrollListener();
    this.setupVariantSync();
    this.setupQuantitySync();
    this.setupFormSync();
    this.setupBottomPadding();
    this.checkInitialVisibility();
  }

  checkInitialVisibility() {
    // Check if add to cart button is visible in viewport
    if (this.mainAddButton) {
      const rect = this.mainAddButton.getBoundingClientRect();
      const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;

      if (!isInViewport && window.scrollY > this.scrollThreshold) {
        this.show();
      }
    }
  }

  setupScrollListener() {
    let ticking = false;

    window.addEventListener("scroll", () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Check if main add button is in viewport
    if (this.mainAddButton) {
      const rect = this.mainAddButton.getBoundingClientRect();
      const isMainButtonVisible =
        rect.top >= 0 && rect.top <= window.innerHeight;

      if (!isMainButtonVisible && scrollTop > this.scrollThreshold) {
        this.show();
      } else if (isMainButtonVisible || scrollTop < this.scrollThreshold) {
        this.hide();
      }
    }

    this.lastScrollTop = scrollTop;
  }

  show() {
    if (!this.isVisible) {
      this.stickyCart.classList.add("is-visible");
      this.isVisible = true;
      // Recalculate padding when shown (in case height changed)
      if (this.updatePaddingCallback) {
        setTimeout(this.updatePaddingCallback, 0);
      }
    }
  }

  hide() {
    if (this.isVisible) {
      this.stickyCart.classList.remove("is-visible");
      this.isVisible = false;
      // Padding remains even when hidden to maintain layout
    }
  }

  setupVariantSync() {
    if (!this.mainVariantInput || !this.stickyVariantInput) return;

    // Listen to main variant selector changes and update sticky cart
    if (this.mainVariantSelector) {
      this.mainVariantSelector.addEventListener("variants:change", (e) => {
        if (e.detail && e.detail.variant) {
          this.syncVariantFromMain(e.detail.variant);
        }
      });
    }

    // Listen to sticky variant selector changes and update main form
    if (this.stickyVariantSelector) {
      this.stickyVariantSelector.addEventListener("variants:change", (e) => {
        if (e.detail && e.detail.variant) {
          this.syncVariantFromSticky(e.detail.variant);
        }
      });
    }

    // Also watch for direct changes to the hidden variant input as backup
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "value"
        ) {
          this.syncVariant();
        }
      });
    });

    observer.observe(this.mainVariantInput, { attributes: true });

    // Also listen for input events
    this.mainVariantInput.addEventListener("change", () => {
      this.syncVariant();
    });

    // Initial sync
    this.syncVariant();
  }

  syncVariantFromMain(variant) {
    // Update the sticky cart's variant input
    if (this.stickyVariantInput && variant.id) {
      this.stickyVariantInput.value = variant.id;
    }

    // Update sticky cart variant selector to match
    if (this.stickyVariantSelector) {
      this.updateVariantSelector(this.stickyVariantSelector, variant.id);
    }

    // Update all the display elements (price, availability, image if variant has featured image)
    this.updateStickyCart(variant);

    // Update image if variant has a featured image
    if (variant.featured_image) {
      this.updateStickyImage(variant.featured_image);
    }
  }

  syncVariantFromSticky(variant) {
    // Update the main form's variant input
    if (this.mainVariantInput && variant.id) {
      this.mainVariantInput.value = variant.id;
    }

    // Update the sticky cart's variant input
    if (this.stickyVariantInput && variant.id) {
      this.stickyVariantInput.value = variant.id;
    }

    // Update main variant selector to match
    if (this.mainVariantSelector) {
      this.updateVariantSelector(this.mainVariantSelector, variant.id);
    }

    // Update all the display elements
    this.updateStickyCart(variant);

    // Update image if variant has a featured image
    if (variant.featured_image) {
      this.updateStickyImage(variant.featured_image);
    }
  }

  updateVariantSelector(selector, variantId) {
    // Get the product-interactive-options element
    const interactiveOptions = selector.querySelector(
      "product-interactive-options"
    );
    if (
      interactiveOptions &&
      typeof interactiveOptions.selectVariantById === "function"
    ) {
      interactiveOptions.selectVariantById(variantId);
    }
  }

  updateStickyImage(featuredImage) {
    const imageElement = this.stickyCart.querySelector(
      ".sticky-add-to-cart__image img"
    );
    if (imageElement && featuredImage) {
      const imageUrl = featuredImage.src || featuredImage;
      imageElement.src = imageUrl;
      if (featuredImage.alt) {
        imageElement.alt = featuredImage.alt;
      }
    }
  }

  syncVariant() {
    if (!this.mainVariantInput || !this.stickyVariantInput) return;

    const variantId = this.mainVariantInput.value;
    this.stickyVariantInput.value = variantId;

    // Note: The main updates will come from the variants:change event
    // This is just a backup to sync the input value if needed
  }

  updateStickyCart(variant) {
    // Update price using Shopify's money formatting
    if (this.priceElement) {
      const priceWrapper = this.priceElement.querySelector(".price");
      if (!priceWrapper) return;

      const moneyFormat =
        window.theme?.money_product_price_format || window.theme?.money_format;
      const isOnSale =
        variant.compare_at_price && variant.compare_at_price > variant.price;

      let priceHTML = `
        <span class="price__number${isOnSale ? " price__number--sale" : ""}">
          <span class="money">${window.Shopify.formatMoney(
            variant.price,
            moneyFormat
          )}</span>
        </span>
      `;

      if (isOnSale) {
        priceHTML += `
          <s class="price__compare">
            <span class="money">${window.Shopify.formatMoney(
              variant.compare_at_price,
              moneyFormat
            )}</span>
          </s>
        `;
      }

      priceWrapper.innerHTML = priceHTML;

      // Update currency if Currency converter exists
      if (window.Currency && window.Currency.convertAll) {
        window.Currency.convertAll(
          window.theme.shopCurrency,
          window.Currency.currentCurrency,
          ".sticky-add-to-cart__price span.money"
        );
      }
    }

    // Update availability
    if (this.stickyAddButton && this.stickyAddText) {
      if (variant.available) {
        this.stickyAddButton.disabled = false;
        this.stickyAddButton.classList.remove("is-disabled");
        // Get the current text from main button if available
        const addToCartText = this.mainAddText?.textContent || "Add to cart";
        this.stickyAddText.textContent = addToCartText;
      } else {
        this.stickyAddButton.disabled = true;
        this.stickyAddButton.classList.add("is-disabled");
        // Check for coming soon metafield
        const soldOutText = this.mainAddText?.textContent || "Sold out";
        this.stickyAddText.textContent = soldOutText;
      }
    }
  }

  setupQuantitySync() {
    if (!this.mainQuantityInput || !this.stickyQuantityInput) return;

    // Sync from main to sticky
    this.mainQuantityInput.addEventListener("change", () => {
      this.stickyQuantityInput.value = this.mainQuantityInput.value;
    });

    // Sync from sticky to main
    this.stickyQuantityInput.addEventListener("change", () => {
      this.mainQuantityInput.value = this.stickyQuantityInput.value;
    });

    // Listen for quantity-input custom events
    const mainQuantityElement =
      this.mainQuantityInput?.closest("quantity-input");
    const stickyQuantityElement =
      this.stickyQuantityInput?.closest("quantity-input");

    if (mainQuantityElement) {
      mainQuantityElement.addEventListener("update", (e) => {
        if (this.stickyQuantityInput) {
          this.stickyQuantityInput.value = e.detail.value;
        }
      });
    }

    if (stickyQuantityElement) {
      stickyQuantityElement.addEventListener("update", (e) => {
        if (this.mainQuantityInput) {
          this.mainQuantityInput.value = e.detail.value;
        }
      });
    }

    // Initial sync
    if (this.mainQuantityInput && this.stickyQuantityInput) {
      this.stickyQuantityInput.value = this.mainQuantityInput.value;
    }
  }

  setupFormSync() {
    if (!this.mainForm || !this.stickyForm) return;

    // Mirror the button states between main and sticky forms
    const mainProductForm = this.mainForm.closest("product-form");

    if (mainProductForm) {
      // Watch for state changes on main add button
      const buttonObserver = new MutationObserver(() => {
        this.syncButtonStates();
      });

      if (this.mainAddButton) {
        buttonObserver.observe(this.mainAddButton, {
          attributes: true,
          attributeFilter: ["class"],
        });
      }
    }
  }

  syncButtonStates() {
    if (!this.mainAddButton || !this.stickyAddButton) return;

    // Sync loading/added states
    if (this.mainAddButton.classList.contains("is-adding")) {
      this.stickyAddButton.classList.add("is-adding");
    } else {
      this.stickyAddButton.classList.remove("is-adding");
    }

    if (this.mainAddButton.classList.contains("is-added")) {
      this.stickyAddButton.classList.add("is-added");
    } else {
      this.stickyAddButton.classList.remove("is-added");
    }

    // Sync disabled state
    if (this.mainAddButton.disabled) {
      this.stickyAddButton.disabled = true;
    } else {
      this.stickyAddButton.disabled = false;
    }
  }

  setupBottomPadding() {
    // Calculate and set bottom padding to prevent footer from being covered
    const updatePadding = () => {
      if (!this.stickyCart) return;

      // Get the actual height of the sticky cart element
      const height = this.stickyCart.offsetHeight;

      // Apply padding to the body to prevent footer from being covered
      if (height > 0) {
        document.documentElement.style.setProperty(
          "--sticky-cart-height",
          `${height}px`
        );
        document.body.style.paddingBottom = `${height}px`;
      }
    };

    // Store the callback so it can be called from other methods
    this.updatePaddingCallback = updatePadding;

    // Initial calculation
    updatePadding();

    // Recalculate on window resize
    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updatePadding, 100);
    });

    // Also recalculate when content might change (e.g., variant selector visibility)
    const observer = new MutationObserver(() => {
      updatePadding();
    });
    observer.observe(this.stickyCart, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new StickyAddToCart();
  });
} else {
  new StickyAddToCart();
}

// Re-initialize on Shopify section re-render
document.addEventListener("shopify:section:load", () => {
  new StickyAddToCart();
});
